/**
 * 재인증 리다이렉트 폭주를 끊는 circuit breaker.
 *
 * 왜 sessionStorage인가 — 이 가드가 막아야 하는 루프는 매 반복이 **새로운 page load**다
 * (인터셉터가 /users/sign_in 으로 보내면 미들웨어가 앱으로 되돌려보내고, 다시 401이 난다).
 * page load마다 JS 모듈이 재평가되므로 모듈 스코프 변수는 매번 0으로 초기화되어 카운트가
 * 불가능하다. 탭 수명 동안 네비게이션을 넘어 유지되는 저장소가 반드시 필요하다.
 *
 * sessionStorage를 못 쓰는 환경(프라이빗 모드, 비활성화)에서는 조용히 통과시킨다 —
 * 가드가 없는 편이, 가드 오류로 리다이렉트를 막아 사용자를 가두는 것보다 낫다.
 */

const STORAGE_KEY = 'linq.reauth-attempts'

/** 이 시간창 안에서 발생한 시도만 센다. */
export const REAUTH_WINDOW_MS = 10_000

/** 이 횟수를 넘어서면 리다이렉트를 멈추고 사용자에게 알린다. */
export const REAUTH_MAX_ATTEMPTS = 3

const readAttempts = (): number[] => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter((n): n is number => typeof n === 'number') : []
  } catch {
    return []
  }
}

const writeAttempts = (attempts: number[]) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts))
  } catch {
    // 저장 실패는 무시한다 — 아래 recordReauthAttempt가 계속 1을 반환해 리다이렉트를 허용한다
  }
}

/**
 * 재인증 리다이렉트 시도를 기록하고, 시간창 안의 누적 횟수를 반환한다.
 * 시간창을 벗어난 기록은 읽는 시점에 버려지므로 따로 정리할 필요가 없다.
 */
export const recordReauthAttempt = (now: number = Date.now()): number => {
  if (typeof window === 'undefined') return 1

  const recent = readAttempts().filter((t) => now - t < REAUTH_WINDOW_MS)
  recent.push(now)
  writeAttempts(recent)

  return recent.length
}

/** 리다이렉트를 더 시도하면 안 되는 상태인지. */
export const isReauthCircuitOpen = (attempts: number): boolean => attempts > REAUTH_MAX_ATTEMPTS

/** 인증이 정상화되었을 때 호출한다. */
export const clearReauthAttempts = () => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // noop
  }
}
